import { CircleAlert, CircleCheck, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { formatPriceDisplay } from '@/util/formatting';

interface BulkImportSummaryProps {
  fileNumber: number | undefined;
  fileNameInfo: string | undefined;
  successData: string | null;
  failedCountInfo: string | null;
  orderTotalValue: number | undefined;
  setActiveStep: (step: number) => void;
}

function StatItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Typography as="span" variant="small" className="text-muted-foreground">
        {label}
      </Typography>
      <Typography as="span" variant="body-medium">
        {children}
      </Typography>
    </div>
  );
}

export function BulkImportSummary({
  fileNumber,
  fileNameInfo,
  successData,
  failedCountInfo,
  orderTotalValue,
  setActiveStep,
}: BulkImportSummaryProps) {
  const failedCount = Number(failedCountInfo);
  const successCount = Number(successData);
  const hasFailed = failedCount > 0;

  return (
    <div className="mt-8 space-y-4">
      <Typography as="h2" variant="heading-md" className="font-semibold">
        Step 3 - Order Summary
      </Typography>

      {hasFailed && (
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <Typography as="p" variant="body">
            Place the order for all accepted orders or fix the errors to place the order all at once.
          </Typography>
        </div>
      )}

      <div className="rounded-md border border-border bg-muted px-5 py-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-6">
          <StatItem label="File ID">{fileNumber}</StatItem>
          <StatItem label="File Name">{fileNameInfo}</StatItem>
          <StatItem label="Total Orders">{successCount + failedCount}</StatItem>

          <div className="flex flex-col gap-1.5">
            <Typography as="span" variant="small" className="text-muted-foreground">
              Order accepted/placed
            </Typography>
            <span className="inline-flex items-center gap-1.5">
              <CircleCheck className="h-4 w-4 text-success" aria-hidden="true" />
              <Typography as="span" variant="body-medium">
                {successData} accepted
              </Typography>
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Typography as="span" variant="small" className="text-muted-foreground">
              Order rejected
            </Typography>
            <span className="inline-flex items-center gap-1.5">
              <CircleAlert className="h-4 w-4 text-destructive" aria-hidden="true" />
              <Typography as="span" variant="body-medium">
                {failedCountInfo} rejected
              </Typography>
              {hasFailed && (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-sm font-medium text-destructive underline max-lg:min-h-11"
                  onClick={() => setActiveStep(2)}
                >
                  Fix errors
                </Button>
              )}
            </span>
          </div>

          <StatItem label="Order Value">${formatPriceDisplay(orderTotalValue ?? 0)}</StatItem>
        </div>
      </div>
    </div>
  );
}
